import {createContext, FC, useState, useEffect, useContext, useRef, type ReactNode} from "react";
import {Socket, io} from "socket.io-client";
import {useAccessToken} from "../hooks/useAccessToken";
import {App2NotificationContext, App2NotificationSeverity} from "./App2NotificationContext";
import {useQueryClient} from "@tanstack/react-query";
import {NOTIFICATIONS_QUERY_KEYS} from "../data/repos/notifications";
import {ANNOUNCEMENTS_QUERY_KEYS} from "../data/repos/announcements";
import {QUERY_KEYS} from "../data/repos/user";
import {PLAYLIST_QUERY_KEYS, PLAYLIST_ENROLLMENTS_QUERY_PREFIX} from "../data/repos/playlists";
import {COURSES_QUERY_KEYS} from "../data/repos/courses";
import {useBulkActionSnackbar} from "./BulkActionSnackbarContext";
import {invalidateLeaderboardQueries} from "../data/repos/leaderboards";
import type {NotificationEvent} from "../data/gen";
import {SocketIoServerEventNameSchemaEventName} from "../data/gen";

const WEBSOCKET_URL = process.env.REACT_APP_IMPROVE_API_URL || "http://localhost:5111";

type WebSocketContextState = {
	socket: Socket | undefined;
	isConnected: boolean;
};

const initialState: WebSocketContextState = {
	socket: undefined,
	isConnected: false
};

export const WebSocketContext = createContext<WebSocketContextState>(initialState);

interface IProps {
	children: ReactNode;
}

/**
 * WebSocketProvider for App2
 * Connects to improve-api gateway using PropelAuth access token.
 * Listens for server push events (see `SocketIoServerEventNameSchemaEventName`) and invalidates React Query caches.
 */
export const WebSocketProvider: FC<IProps> = ({children}) => {
	const [socket, setSocket] = useState<Socket | undefined>(undefined);
	const [isConnected, setIsConnected] = useState(false);
	const accessToken = useAccessToken();
	const {notify} = useContext(App2NotificationContext);
	const queryClient = useQueryClient();
	const {closeSnackbar: closeBulkSnackbar} = useBulkActionSnackbar();
	const silentCourseEnrollRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Keep latest callbacks without re-running the socket effect (reconnecting) when their identity changes.
	const notifyRef = useRef(notify);
	const closeBulkSnackbarRef = useRef(closeBulkSnackbar);
	notifyRef.current = notify;
	closeBulkSnackbarRef.current = closeBulkSnackbar;

	useEffect(() => {
		if (!accessToken) {
			setIsConnected(false);
			return;
		}

		// Create socket connection with PropelAuth token (same as improve-service)
		const newSocket = io(WEBSOCKET_URL, {
			auth: {
				token: accessToken
			},
			transports: ["websocket", "polling"]
		});

		// Connection handlers
		newSocket.on("connect", () => {
			setIsConnected(true);
		});

		newSocket.on("disconnect", (reason) => {
			setIsConnected(false);
		});

		newSocket.on("connect_error", (error) => {
			console.error("WebSocket connection error:", error);
			setIsConnected(false);
		});

		// Real-time notification: show toast and refresh notification list
		const handleNotification = (e: NotificationEvent) => {
			const subject = e?.data?.subject ?? "";
			const bodyRaw = e?.data?.body ?? "";
			const parser = new DOMParser();
			const doc = parser.parseFromString(bodyRaw, "text/html");
			const body = doc.body.textContent ?? bodyRaw;
			notifyRef.current(body, subject, App2NotificationSeverity.neutral, 5000, false);
			queryClient.invalidateQueries({queryKey: [NOTIFICATIONS_QUERY_KEYS.LIST]});
			queryClient.invalidateQueries({queryKey: [ANNOUNCEMENTS_QUERY_KEYS.LEARNER_ASSIGNED]});
			queryClient.invalidateQueries({queryKey: [ANNOUNCEMENTS_QUERY_KEYS.LEARNER_NOTIFICATIONS]});
		};
		newSocket.on(SocketIoServerEventNameSchemaEventName.notification, handleNotification);

		// Bulk enrollment / unenrollment completions (REST + jobs): refresh feed and enrolled courses
		const handleEnrolledCoursesRefresh = () => {
			queryClient.invalidateQueries({queryKey: [QUERY_KEYS.FEED]});
			queryClient.invalidateQueries({queryKey: [QUERY_KEYS.ENROLLED_COURSES]});
			queryClient.invalidateQueries({queryKey: [QUERY_KEYS.ACTIVE_COURSES]});
		};

		/** Matches `useListCourseEnrollments` query keys (admin course enrollment grid, due dates, etc.). */
		const invalidateCourseEnrollmentLists = () => {
			queryClient.invalidateQueries({
				predicate: (q) =>
					Array.isArray(q.queryKey) && q.queryKey[0] === "courses" && q.queryKey[1] === "enrollments"
			});
		};

		type BulkCompletionPayload = {
			data?: { isSuccessful?: boolean; errorMessage?: string; silentRefresh?: boolean };
		};

		const flushSilentCourseEnrollRefresh = (payload: {
			isSuccessful: boolean;
			errorMessage?: string;
		}) => {
			handleEnrolledCoursesRefresh();
			invalidateCourseEnrollmentLists();
			window.dispatchEvent(
				new CustomEvent("courseEnrollmentsCompleted", {
					detail: {
						inProgress: false,
						isSuccessful: payload.isSuccessful,
						errorMessage: payload.errorMessage,
						silentRefresh: true
					}
				})
			);
		};

		const handleCourseEnrollmentsSocket = (e?: BulkCompletionPayload) => {
			const isSuccessful = e?.data?.isSuccessful !== false;
			const errorMessage = e?.data?.errorMessage;
			const silentRefresh = e?.data?.silentRefresh === true;

			if (silentRefresh) {
				if (silentCourseEnrollRefreshTimerRef.current) {
					clearTimeout(silentCourseEnrollRefreshTimerRef.current);
				}
				silentCourseEnrollRefreshTimerRef.current = setTimeout(() => {
					silentCourseEnrollRefreshTimerRef.current = null;
					flushSilentCourseEnrollRefresh({isSuccessful, errorMessage});
				}, 400);
				return;
			}

			handleEnrolledCoursesRefresh();
			invalidateCourseEnrollmentLists();
			window.dispatchEvent(
				new CustomEvent("courseEnrollmentsCompleted", {
					detail: {
						inProgress: false,
						isSuccessful,
						errorMessage
					}
				})
			);
		};

		const handleUnenrollmentsSocket = (_e?: BulkCompletionPayload) => {
			handleEnrolledCoursesRefresh();
			invalidateCourseEnrollmentLists();
			// No window event: admin course grid relies on HTTP + query invalidation for course row state;
			// WS may arrive after lesson cascade and is not needed to refresh that UI.
		};

		newSocket.on(SocketIoServerEventNameSchemaEventName.completedCourseEnrollments, handleCourseEnrollmentsSocket);
		newSocket.on(SocketIoServerEventNameSchemaEventName.completedPlaylistEnrollments, handleEnrolledCoursesRefresh);
		newSocket.on(SocketIoServerEventNameSchemaEventName.completedUnenrollments, handleUnenrollmentsSocket);
		newSocket.on(SocketIoServerEventNameSchemaEventName.completedRescheduleOverdueModules, handleEnrolledCoursesRefresh);

		// Bulk playlist completion: close processing snackbar, show result toast, refresh playlist data
		const handlePlaylistBulkComplete = (e: {data: {isSuccessful: boolean}}) => {
			closeBulkSnackbarRef.current("playlistBulkComplete");
			if (e?.data?.isSuccessful) {
				notifyRef.current("Playlist bulk completion finished successfully.", "", App2NotificationSeverity.success, 5000, false);
			} else {
				notifyRef.current("Playlist bulk completion failed.", "", App2NotificationSeverity.error, 5000, false);
			}
			queryClient.invalidateQueries({queryKey: [PLAYLIST_QUERY_KEYS.LIST]});
			queryClient.invalidateQueries({queryKey: [...PLAYLIST_ENROLLMENTS_QUERY_PREFIX]});
		};
		newSocket.on(SocketIoServerEventNameSchemaEventName.completedPlaylistBulkComplete, handlePlaylistBulkComplete);

		// Bulk course completion: close processing snackbar, show result toast, refresh course data
		const handleCourseBulkComplete = (e: {data: {isSuccessful: boolean}}) => {
			closeBulkSnackbar("courseBulkComplete");
			if (e?.data?.isSuccessful) {
				notify("Course bulk completion finished successfully.", "", App2NotificationSeverity.success, 5000, false);
			} else {
				notify("Course bulk completion failed.", "", App2NotificationSeverity.error, 5000, false);
			}
			queryClient.invalidateQueries({queryKey: [COURSES_QUERY_KEYS.LIST]});
			queryClient.invalidateQueries({queryKey: ["courses", "enrollments"]});
		};
		newSocket.on(SocketIoServerEventNameSchemaEventName.completedCourseBulkComplete, handleCourseBulkComplete);

		const handleLeaderboardUpdated = (event: unknown) => {
			invalidateLeaderboardQueries(queryClient, event);
		};
		newSocket.on(SocketIoServerEventNameSchemaEventName.leaderboardUpdated, handleLeaderboardUpdated);

		setSocket(newSocket);

		// Cleanup on unmount
		return () => {
			if (silentCourseEnrollRefreshTimerRef.current) {
				clearTimeout(silentCourseEnrollRefreshTimerRef.current);
				silentCourseEnrollRefreshTimerRef.current = null;
			}
			newSocket.off(SocketIoServerEventNameSchemaEventName.notification, handleNotification);
			newSocket.off(SocketIoServerEventNameSchemaEventName.completedCourseEnrollments, handleCourseEnrollmentsSocket);
			newSocket.off(SocketIoServerEventNameSchemaEventName.completedPlaylistEnrollments, handleEnrolledCoursesRefresh);
			newSocket.off(SocketIoServerEventNameSchemaEventName.completedUnenrollments, handleUnenrollmentsSocket);
			newSocket.off(SocketIoServerEventNameSchemaEventName.completedRescheduleOverdueModules, handleEnrolledCoursesRefresh);
			newSocket.off(SocketIoServerEventNameSchemaEventName.completedPlaylistBulkComplete, handlePlaylistBulkComplete);
			newSocket.off(SocketIoServerEventNameSchemaEventName.completedCourseBulkComplete, handleCourseBulkComplete);
			newSocket.off(SocketIoServerEventNameSchemaEventName.leaderboardUpdated, handleLeaderboardUpdated);
			newSocket.disconnect();
			newSocket.close();
		};
	}, [accessToken, queryClient]);

	// Unauthenticated routes (e.g. landing / login): no socket — still render children.
	// Do not block the tree on a spinner; `useEffect` above skips `io()` when `!accessToken`.
	if (!accessToken) {
		return (
			<WebSocketContext.Provider value={{socket: undefined, isConnected: false}}>
				{children}
			</WebSocketContext.Provider>
		);
	}

	return (
		<WebSocketContext.Provider value={{socket, isConnected}}>
			{children}
		</WebSocketContext.Provider>
	);
};

/**
 * Custom hook to use the WebSocket context
 * @returns {WebSocketContextState} Socket instance and connection status
 */
export const useWebSocket = (): WebSocketContextState => {
	const context = useContext(WebSocketContext);
	if (context === undefined) {
		throw new Error("useWebSocket must be used within a WebSocketProvider");
	}
	return context;
};

